import { notificationsAPI } from './api';

export type PushStatus = {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
  /** iOS/iPadOS instalado como PWA (pantalla de inicio), requerido para push en Apple */
  standalone: boolean;
  /** Safari en iPhone/iPad */
  isIOS: boolean;
  /** Motivo legible si supported === false */
  reason?: string;
};

const PERMISSION_TIMEOUT_MS = 10000;

export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    nav.standalone === true
  );
}

function hasPushApis(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function getUnsupportedReason(): string | undefined {
  if (!hasPushApis()) {
    if (isIOSDevice() && !isStandalonePWA()) {
      return 'En iPhone: abre la app desde el icono de pantalla de inicio (Safari → Compartir → Añadir a pantalla de inicio). No funciona solo en una pestaña de Safari.';
    }
    return 'Este navegador no expone Service Worker o Push API.';
  }
  return undefined;
}

function isSupported(): boolean {
  if (!hasPushApis()) return false;
  if (isIOSDevice() && !isStandalonePWA()) return false;
  return true;
}

function isSecurePushContext(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return window.isSecureContext || host === 'localhost' || host === '127.0.0.1';
}

async function requestPermissionWithTimeout(): Promise<NotificationPermission> {
  return Promise.race([
    Notification.requestPermission(),
    new Promise<NotificationPermission>((_resolve, reject) => {
      window.setTimeout(() => reject(new Error('Timeout solicitando permiso de notificaciones')), PERMISSION_TIMEOUT_MS);
    }),
  ]);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/** Registrar SW al cargar la app (iOS lo requiere antes de suscribirse). */
export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing) return existing;
  const registered = await registerPushServiceWorker();
  if (!registered) throw new Error('No se pudo registrar el service worker');
  return registered;
}

export async function getPushStatus(): Promise<PushStatus> {
  const ios = isIOSDevice();
  const standalone = isStandalonePWA();
  const reason = getUnsupportedReason();

  if (!isSupported() || !isSecurePushContext()) {
    return {
      supported: false,
      permission: 'unsupported',
      subscribed: false,
      standalone,
      isIOS: ios,
      reason: !isSecurePushContext()
        ? 'Push requiere HTTPS (o localhost).'
        : reason,
    };
  }

  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();

  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!subscription,
    standalone,
    isIOS: ios,
  };
}

export async function syncPushSubscription(): Promise<boolean> {
  if (!isSupported() || !isSecurePushContext() || Notification.permission !== 'granted') {
    return false;
  }

  const registration = await getRegistration();
  const existing = await registration.pushManager.getSubscription();

  if (existing) {
    await notificationsAPI.subscribePush(existing.toJSON() as Record<string, unknown>);
    return true;
  }

  const keyResponse = await notificationsAPI.pushPublicKey();
  const publicKey = keyResponse.data?.publicKey as string | undefined;

  if (!publicKey) {
    return false;
  }

  const created = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
  });

  await notificationsAPI.subscribePush(created.toJSON() as Record<string, unknown>);
  return true;
}

export async function enablePushNotifications(): Promise<boolean> {
  if (!isSupported() || !isSecurePushContext()) {
    return false;
  }

  const permission = await requestPermissionWithTimeout();
  if (permission !== 'granted') {
    return false;
  }

  return syncPushSubscription();
}

export async function disablePushNotifications(): Promise<boolean> {
  if (!isSupported() || !isSecurePushContext()) {
    return false;
  }

  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    return true;
  }

  const endpoint = subscription.endpoint;
  await notificationsAPI.unsubscribePush(endpoint);
  await subscription.unsubscribe();

  return true;
}
